'use client';

/**
 * Binding context — the indirection that keeps @inferrex/ui app-agnostic.
 *
 * The app-runtime blocks (CollectionBlock / RecordBlock / form) need to READ and
 * WRITE entity data, but @inferrex/ui must NEVER import an app's api-client
 * (that would couple the shared library to one app's auth/transport). So the app
 * supplies a `DataBinding` once at its root; the blocks consume it via context.
 *
 * The binding itself is codegen — a generator emits the per-entity client wiring
 * (list/get/create/update/remove keyed by entity, over the generated typed
 * clients / @inferrex/sdk). No hand-written per-entity data wiring; the runtime
 * here is the only hand-written piece (Rule 33), like FormRegistry's provider.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { ComboboxOption } from '../primitives/combobox';
import type { FieldSource } from '../semantic/field-source';
import type { TrustBadgeTier } from '../primitives/blocks/TrustBadge';
import type { ScoreThresholds } from '../semantic/status-tone';

export type EntityRow = Record<string, unknown>;

/** A connection's host-computed trust tier + score (see DataBinding.getTrustScore). */
export interface TrustScore {
  readonly tier: TrustBadgeTier;
  readonly score: number;
}

/** The onboarding ledger's derived progress (see DataBinding.getOnboardingProgress). */
export interface OnboardingProgressData {
  readonly nextStep: { readonly label: string; readonly path: string } | null;
  readonly completedCount: number;
  readonly totalSteps: number;
  readonly isComplete: boolean;
}

/**
 * A server-side list query (filter/sort/group/facets/page) — the wire-agnostic
 * shape `EntityBinding.query` accepts. Wire keys for `filters` follow the crud
 * route generator's operator-param convention: `col`, `col_contains`, `col_gte`,
 * `col_lte`, `col_in` (comma-joined for multi-select).
 */
export interface ListQuery {
  readonly filters?: Readonly<Record<string, string | number>>;
  readonly sort?: string;
  readonly order?: 'asc' | 'desc';
  /** group-by column — the response returns `groups` instead of `rows`. */
  readonly group?: string;
  /** facet columns to compute counts for (comma-joined on the wire). */
  readonly facets?: readonly string[];
  /** Free-text search (`q` on the wire): rows where any text column contains it. */
  readonly search?: string;
  readonly limit?: number;
  readonly offset?: number;
  /** Keyset page token from a previous result's `nextCursor`; replaces `offset` when set. */
  readonly cursor?: string;
}

/**
 * The response shape for `EntityBinding.query` — rows plus optional total/facets/groups,
 * and the paging signal: `total` on an offset list, `nextCursor` + `hasMore` on a keyset
 * list. A result with none of the three is one complete page.
 */
export interface ListResult {
  readonly rows: EntityRow[];
  readonly total?: number;
  readonly facets?: Record<string, ReadonlyArray<{ readonly value: unknown; readonly count: number }>>;
  readonly groups?: ReadonlyArray<{ readonly key: unknown; readonly count: number }>;
  readonly truncated?: boolean;
  readonly nextCursor?: string | null;
  readonly hasMore?: boolean;
}

/** CRUD operations for one entity. Optional ops are absent when the route lacks them. */
export interface EntityBinding {
  /** The FIRST page only (the route's default page size). Use `query` to page further. */
  list(): Promise<EntityRow[]>;
  /**
   * Server-side filter/sort/group/paginate — available on any listable (i.e.
   * non-singleton, non-parent-scoped) entity binding, not gated on the route's
   * `serverQuery` capability. It forwards allow-listed filter/sort/group/facet
   * params and parses the richer envelope, but `total`/`facets`/`groups` are
   * only populated when the route opted into `serverQuery`; against a plain
   * route the backend strips the unknown params and this just resolves with
   * `{ rows }`. Additive: `list()` is untouched for callers that don't need
   * server-side querying.
   */
  query?(q: ListQuery): Promise<ListResult>;
  get?(id: string): Promise<EntityRow | null>;
  create?(values: EntityRow): Promise<unknown>;
  update?(id: string, values: EntityRow): Promise<unknown>;
  remove?(id: string): Promise<unknown>;
  /**
   * List a PARENT-SCOPED computed collection — rows filtered by a parent record
   * id taken from the route path (e.g. a pipeline's suggestions / sync-history /
   * anomalies, served by `/v1/pipelines/:id/<sub>`). Present only when the binding
   * declares a `parentParam`; `list()` is unavailable for these (no parent id).
   */
  listScoped?(parentId: string): Promise<EntityRow[]>;
}

/** The app-supplied data layer. `forEntity` resolves the per-entity client. */
export interface DataBinding {
  /** Resolve the binding for an entity key (the manifest's `entity`). */
  forEntity(entity: string): EntityBinding;
  /** Load option rows for a `relation` field that targets `entity`, optionally narrowed by
   *  column-equality `filters` (sent as the list route's own query filters). */
  loadRelationOptions?(entity: string, query: string, filters?: Readonly<Record<string, string>>): Promise<readonly ComboboxOption[]>;
  /**
   * Fetch an entity's declared-or-inferred field source at runtime
   * (`GET /v1/schema/:entity`). Lets DynamicEntityPage render an entity with NO
   * generated page file — the same path that renders a virgin API's inferred
   * schema live.
   */
  getSchema?(entity: string): Promise<FieldSource>;
  /**
   * Invoke a declarative action endpoint (an `app.actions` entry) — e.g. an
   * approval queue's approve/reject. The endpoint is already row-substituted by
   * the caller; `body` is the action's fixed payload.
   */
  runAction?(endpoint: string, method: string, body?: Record<string, unknown>): Promise<unknown>;
  /**
   * Compute a connection's trust score for a provider `systemKey`. The host fetches provider
   * metadata from `GET /v1/library/:systemKey` and runs `computeTrustScore`; returns null when the
   * connector library has no entry for the key. Consumed by the card-list `trustBadge` cell — kept
   * on the binding (not @inferrex/ui) so the library stays free of the trust-score data dependency.
   */
  getTrustScore?(systemKey: string): Promise<TrustScore | null>;
  /**
   * The tenant's resolved health-score band thresholds (the `health.scoreHealthy` /
   * `health.scoreWarning` platform-config tunables). Consumed by the `health-grid` block
   * so the green/amber/red cutoffs honour the admin/customer override — kept on the
   * binding (not @inferrex/ui) so the library stays free of the platform-config dependency.
   */
  getScoreThresholds?(): Promise<ScoreThresholds>;
  /**
   * The onboarding ledger's derived progress (server `GET /v1/onboarding` mapped over
   * the fixed step list). Consumed by the `dashboard-hero` block to front continue-setup
   * vs create — kept on the binding (not @inferrex/ui) so the library stays free of the
   * onboarding data dependency (mirrors getScoreThresholds/getTrustScore).
   */
  getOnboardingProgress?(): Promise<OnboardingProgressData>;
}

const BindingContext = createContext<DataBinding | null>(null);

export function BindingProvider({ binding, children }: { binding: DataBinding; children: ReactNode }) {
  return <BindingContext.Provider value={binding}>{children}</BindingContext.Provider>;
}

/** The active DataBinding — throws if no provider is mounted (a wiring bug). */
export function useBinding(): DataBinding {
  const binding = useContext(BindingContext);
  if (!binding) throw new Error('useBinding: no <BindingProvider> in the tree. Mount the generated app binding at the app root.');
  return binding;
}

/** Convenience: the binding for one entity. */
export function useEntityBinding(entity: string): EntityBinding {
  return useBinding().forEntity(entity);
}
